import { useRouter } from 'next/router';
import { IconID } from '@common/Icon/Icon';
import { Link } from '@common/Link/Link';
import { WidenImage } from '@common/Widen/WidenImage';
import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import type { LabeledAttribute, ProductAttribute } from '@libs/attribute.helper';
import { getAdditionalAttributeValue, getAttributeValue } from '@libs/attribute.helper';
import { getStandardMountingVariant } from '@libs/echain';
import { MediaElementType } from '@libs/widen-utils';
import type { AssociationProductFragmentEnhanced, ProductAssetsFragmentEnhanced } from '@models/enhanced-fragments';
import { ProductHelper } from '@models/graphql';
import { ProductMediaTeaser } from '@product/ProductMediaTeaser/ProductMediaTeaser';
import { ProductUsp } from '@product/ProductUsp/ProductUsp';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { SelectorBox } from '../SelectorBox/SelectorBox';
import styles from './MountingBracketSelector.module.scss';
import { getLocale } from '@libs/constants';
import { getProductIdForLocale } from '@libs/product.helper';

// Mounting brackets have a 3-layered hierarchy. At the most abstract layer there are groups (e.g. "Kunststoff, pendelnd", "KMA, starr").
// These are divided into several distinct variants (e.g. "ohne Kämme", "mit Kämmen").
// The variants are further divided into mounting variants (e.g. "A1", "A2")
// The types used to represent this hierarchy are MountingBracketGroup --> DistinctVariant --> AssociationProductFragmentEnhanced
export interface MountingBracketGroup {
  key: string;
  displayVariant: AssociationProductFragmentEnhanced;
  distinctVariants: DistinctVariant[];
}

export interface DistinctVariant {
  key: string;
  id: string;
  id_us: string;
  seriesDescription?: ProductAttribute<string>;
  assets?: Array<ProductAssetsFragmentEnhanced>;
  mountingVariants?: AssociationProductFragmentEnhanced[];
  // when no mountingVariants are given, we need a defaultVariant to use in their stead. (for displaying correct names and allowing to be selected)
  defaultVariant?: AssociationProductFragmentEnhanced;
}

export const MountingBracketSelector = () => {
  const { t } = useTranslation(['product/echainBuyBox']);

  const router = useRouter();
  const locale = getLocale(router);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const selectedMountingBracketGroup = useEchainProductStore(state => state.selectedMountingBracketGroup);
  const selectedDistinctVariant = useEchainProductStore(state => state.selectedDistinctVariant);
  const selectedMountingVariant = useEchainProductStore(state => state.selectedMountingVariant);
  const groupedMountingBrackets = useEchainProductStore(state => state.groupedMountingBrackets);
  const echainAttributeDefinitions = useProductPageStore(state => state.echainAttributeDefinitions);
  const setSelectedMountingBracketGroup = useEchainProductStore(state => state.setSelectedMountingBracketGroup);
  const setSelectedDistinctVariant = useEchainProductStore(state => state.setSelectedDistinctVariant);
  const setSelectedMountingVariant = useEchainProductStore(state => state.setSelectedMountingVariant);
  const clearSelectedMountingBrackets = useEchainProductStore(state => state.clearSelectedMountingBrackets);
  const setHasUserDeselectedMountingBrackets = useEchainProductStore(
    state => state.setHasUserDeselectedMountingBrackets
  );

  const mountingBracketGroupCount: number = groupedMountingBrackets?.length ?? 0;

  const mediaTeaser =
    selectedMountingVariant &&
    ProductHelper.getAssetsMedia(selectedMountingVariant).productMedia.filter(
      media => media.type === MediaElementType.DRAWING
    )[0];

  return (
    <div data-testid="mountingBracketSelector">
      {selectedMountingBracketGroup && (
        <div className={`col-span-${(groupedMountingBrackets?.length || '').toString()} mb-8`}>
          <Link
            className="text-gray-500 w-max text-sm"
            onClick={() => {
              setHasUserDeselectedMountingBrackets(true);
              clearSelectedMountingBrackets(currentProduct.id);
            }}
            icon={IconID.CLOSE_CROSS}
            text={t('ECHAIN_PRODUCT_PAGE.MOUNTING_BRACKETS.REMOVE_MOUNTING_BRACKETS')}
            bigIcon
          />
        </div>
      )}
      <div
        data-testid="mountingBracketGroups"
        className={classNames(
          'grid',
          'grid-cols-1',
          `sm:grid-cols-2`,
          `lg:grid-cols-${mountingBracketGroupCount >= 3 ? '3' : '2'}`,
          mountingBracketGroupCount > 1 && 'gap-8',
          'mb-8'
        )}
      >
        {groupedMountingBrackets?.map((mountingBracketGroup, key) => {
          // render SelectorBoxes for mountingBracketGroups
          let teaserImage = ProductHelper.getTeaserImage(mountingBracketGroup.displayVariant);

          if (selectedMountingBracketGroup?.key === mountingBracketGroup?.key && selectedDistinctVariant) {
            teaserImage = ProductHelper.getTeaserImageFromRawData(
              selectedDistinctVariant?.seriesDescription,
              selectedDistinctVariant?.assets
            );
          }

          const selectorLabel = `${getAttributeValue<string>(
            mountingBracketGroup.displayVariant.attributes?.mounting_bracket_type_2 as ProductAttribute<string>
          )}, ${getAttributeValue<string>(
            mountingBracketGroup.displayVariant.attributes?.allignment_of_mounting_bracket as ProductAttribute<string>
          )}`;
          let mountingBracketSku = getProductIdForLocale(mountingBracketGroup.displayVariant, locale);

          if (selectedMountingBracketGroup?.key === mountingBracketGroup?.key && selectedMountingVariant) {
            mountingBracketSku = getProductIdForLocale(selectedMountingVariant, locale);
          } else if (mountingBracketGroup.distinctVariants[0].mountingVariants) {
            const standardMountingVariant = getStandardMountingVariant(mountingBracketGroup.distinctVariants[0]);
            mountingBracketSku = standardMountingVariant
              ? getProductIdForLocale(standardMountingVariant, locale)
              : mountingBracketSku;
          }

          return (
            <SelectorBox
              /* eslint-disable-next-line react/no-array-index-key */
              key={key}
              dataTestId="selector-box"
              objectKey={key}
              selected={mountingBracketGroup.key === selectedMountingBracketGroup?.key}
              onSelect={() => {
                /*
                  when changing the mounting bracket, set the distinct variant to the first distinct variant
                  and set the mounting variant to the standard mounting variant
                  else do nothing
                 */
                if (!selectedMountingBracketGroup || selectedMountingBracketGroup.key !== mountingBracketGroup.key) {
                  setSelectedMountingBracketGroup(mountingBracketGroup);
                  const variant = mountingBracketGroup.distinctVariants[0];
                  setSelectedDistinctVariant(variant);
                  setSelectedMountingVariant(getStandardMountingVariant(variant));
                }
              }}
            >
              <p className="mb-8 mt-3 leading-8">
                <strong>{selectorLabel}</strong>
                <br />
                <small className="text-gray-300" data-testid="mountingBracketsSku">
                  {mountingBracketSku}
                </small>
              </p>
              <div
                className={classNames(styles.mountingBracketImgWrapper, 'overflow-hidden', 'h-0', 'relative', 'mb-2')}
              >
                <WidenImage
                  shortCode={teaserImage?.shortCode}
                  title={teaserImage?.title}
                  filename={teaserImage?.filename}
                  width={0}
                  height={0}
                  layout="fill"
                />
              </div>
            </SelectorBox>
          );
        })}
      </div>
      {selectedMountingBracketGroup && selectedMountingBracketGroup.distinctVariants.length > 0 && (
        <div>
          <h3 className="mb-8 font-bold text-sm">{t('ECHAIN_PRODUCT_PAGE.MOUNTING_BRACKETS.SELECT_VARIANT')}</h3>
          <div
            data-testid="distinctVariants"
            className={`grid grid-cols-1 sm:grid-cols-${selectedMountingBracketGroup.distinctVariants.length} lg:grid-cols-${selectedMountingBracketGroup.distinctVariants.length} gap-8 mb-8`}
          >
            {selectedMountingBracketGroup.distinctVariants.map((variant: DistinctVariant, key) => {
              // render SelectorBoxes for distinctVariants
              const standardMountingVariant = getStandardMountingVariant(variant);

              const distinctVariantText = standardMountingVariant
                ? getAttributeValue<string>(
                    standardMountingVariant.attributes?.variant_of_mounting_bracket as ProductAttribute<string>
                  )
                : getAttributeValue<string>(
                    variant.defaultVariant?.attributes?.variant_of_mounting_bracket as ProductAttribute<string>
                  );

              return (
                <SelectorBox
                  key={variant.key}
                  objectKey={key}
                  selected={variant.key === selectedDistinctVariant?.key}
                  onSelect={() => {
                    // when changing the variant, set the mounting variant to the standard mounting variant of the new variant
                    setSelectedDistinctVariant(variant);
                    setSelectedMountingVariant(standardMountingVariant || variant.defaultVariant);
                  }}
                >
                  {distinctVariantText}
                </SelectorBox>
              );
            })}
          </div>
          {selectedMountingVariant && (
            <div>
              <h3 className="mt-16 mb-8 font-bold text-md">
                {getAttributeValue<string>(
                  selectedMountingVariant.attributes?.series_description as ProductAttribute<string>
                )}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-4 mb-8">
                <div className="col-span-3 pr-8 text-sm">
                  <ProductUsp
                    usp={getAttributeValue<string>(
                      selectedMountingVariant.attributes?.attr_USP as ProductAttribute<string>
                    )}
                  />
                </div>
                <div className="flex justify-end">
                  <div className="w-full max-w-[200px]">
                    {mediaTeaser && (
                      <ProductMediaTeaser
                        media={
                          ProductHelper.getAssetsMedia(selectedMountingVariant).productMedia.filter(
                            media => media.type === MediaElementType.DRAWING
                          )[0]
                        }
                        width={200}
                        height={Math.round((200 / 3) * 2)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {selectedMountingBracketGroup &&
        selectedDistinctVariant &&
        selectedDistinctVariant?.mountingVariants &&
        selectedDistinctVariant?.mountingVariants.length > 0 &&
        selectedMountingVariant && (
          <div>
            <h5 className="mb-8 font-bold">{t('ECHAIN_PRODUCT_PAGE.MOUNTING_BRACKETS.SELECT_MOUNTING_VARIANT')}</h5>
            <div data-testid="mountingVariants" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {selectedDistinctVariant?.mountingVariants.map(
                (mountingVariant: AssociationProductFragmentEnhanced, key) => {
                  // render SelectorBoxes for mountingVariants
                  const variantText = getAttributeValue<string>(
                    mountingVariant.attributes?.mounting_variant as LabeledAttribute<string>
                  );
                  const assetAttribute = getAdditionalAttributeValue(
                    mountingVariant.attributes?.mounting_variant_icon as ProductAttribute<LabeledAttribute<string>>,
                    echainAttributeDefinitions,
                    'mounting_variant_icon',
                    'asset'
                  );
                  const variantIconWidenImage =
                    assetAttribute && ProductHelper.convertImageUriToWidenImage(assetAttribute, variantText);
                  return (
                    <SelectorBox
                      key={variantText}
                      objectKey={key}
                      selected={mountingVariant.id === selectedMountingVariant.id}
                      onSelect={() => {
                        setSelectedMountingVariant(mountingVariant);
                      }}
                    >
                      <p className={classNames(assetAttribute ? 'mb-8' : '')}>{variantText}</p>
                      {assetAttribute && variantIconWidenImage && (
                        <WidenImage
                          shortCode={variantIconWidenImage.shortCode}
                          title={variantIconWidenImage.title}
                          filename={variantIconWidenImage.filename}
                          width={100}
                          height={100}
                        />
                      )}
                    </SelectorBox>
                  );
                }
              )}
            </div>
          </div>
        )}
    </div>
  );
};
